"use client";

import { useState, memo, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Palette } from "lucide-react";
import dynamic from "next/dynamic";
import type { AiProfile } from "@/types/ai";
import { haptics } from "@/lib/utils/haptics";
import { eventBus } from "@/lib/utils/eventBus";
import { useMemo } from "react";
import type { ProactiveTrigger, RaisedHand } from "@/lib/mindaloo/proactiveTriggers";

const ChatInterface = dynamic(() => import("@/components/chat/ChatInterface"), {
  ssr: false,
  loading: () => (
    <div className="h-full flex items-center justify-center text-xs text-black/40">
      <div className="w-5 h-5 border-2 border-black/20 border-t-black/60 rounded-full animate-spin" />
    </div>
  ),
});
const VoiceInputButton = dynamic(() => import("@/components/chat/VoiceInputButton"), { ssr: false });

interface StickyColorOption {
  label: string;
  bg: string;
  border: string;
  text: string;
}

const STICKY_COLORS: StickyColorOption[] = [
  { label: "Void",   bg: "rgba(10,10,18,0.97)",   border: "rgba(255,255,255,0.07)", text: "rgba(255,255,255,0.8)" },
  { label: "Grape",  bg: "rgba(88,28,135,0.92)",  border: "rgba(167,139,250,0.3)",  text: "rgba(255,255,255,0.85)" },
  { label: "Indigo", bg: "rgba(30,27,75,0.95)",   border: "rgba(99,102,241,0.35)",  text: "rgba(255,255,255,0.85)" },
  { label: "Ocean",  bg: "rgba(12,74,110,0.93)",  border: "rgba(56,189,248,0.3)",   text: "rgba(255,255,255,0.85)" },
  { label: "Forest", bg: "rgba(5,46,22,0.93)",    border: "rgba(74,222,128,0.3)",   text: "rgba(255,255,255,0.85)" },
  { label: "Ember",  bg: "rgba(69,10,10,0.94)",   border: "rgba(248,113,113,0.3)",  text: "rgba(255,255,255,0.85)" },
  { label: "Sand",   bg: "rgba(245,235,200,0.97)", border: "rgba(180,140,60,0.3)",  text: "#1a1a1a" },
  { label: "Chalk",  bg: "rgba(252,250,245,0.97)", border: "rgba(200,190,170,0.3)", text: "#1a1a1a" },
];

interface Props {
  profile: AiProfile;
  initialPrompt?: string;
  tileId: string;
  theme?: "night" | "day";
}

export default memo(function MindalooChatTile({ profile, initialPrompt, tileId, theme = "night" }: Props) {
  const accent = profile.brand?.primaryColor ?? "#C1A2FF";

  const [stickyMode, setStickyMode] = useState(false);
  const [stickyColor, setStickyColor] = useState(STICKY_COLORS[0]);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [stickyNote, setStickyNote] = useState("");
  const [externalTrigger, setExternalTrigger] = useState<{ message: string; nonce: number } | null>(null);
  const [injectedAssistantMessage, setInjectedAssistantMessage] = useState<{ content: string; nonce: number } | null>(null);
  const [injectedUserMessage, setInjectedUserMessage] = useState<{ content: string; nonce: number } | null>(null);
  const [conferringLabel, setConferringLabel] = useState<string | null>(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [tileCount, setTileCount] = useState(1);
  // Multichat cross-tile awareness: when this tile is part of the linked
  // group, ChatInterface's own Neural Link mechanism (neuralLinkStore)
  // injects what the *other* linked tiles most recently said into this
  // tile's system prompt — so it can actually reference and build on them,
  // not just answer the same question in isolation. Empty whenever this
  // tile isn't linked, which is also what keeps a stray unrelated tile you
  // have open from picking up context from a conversation it's not part of.
  const [connectedChatIds, setConnectedChatIds] = useState<string[]>([]);
  useEffect(() => {
    const unsub = eventBus.on("SPATIAL:LINKED_TILES_CHANGED", ({ linkedTileIds }: { linkedTileIds: string[] }) => {
      setConnectedChatIds(linkedTileIds.includes(tileId) ? linkedTileIds.filter((id) => id !== tileId) : []);
    });
    return unsub;
  }, [tileId]);
  // Primary tile designation (see MindalooShell.tsx) — passed straight
  // through to ChatInterface, which forwards it into neuralLinkStore so
  // this group's peers know whose take to weigh more.
  const [primaryTileId, setPrimaryTileId] = useState<string | null>(null);
  useEffect(() => {
    const unsub = eventBus.on("SPATIAL:PRIMARY_TILE_CHANGED", ({ primaryTileId }: { primaryTileId: string | null }) => {
      setPrimaryTileId(primaryTileId);
    });
    return unsub;
  }, []);
  // Ref, not state — only read inside the message-complete callback below,
  // never rendered, so it doesn't need to trigger a re-render on change.
  const isActiveRef = useRef(false);

  // Real per-tile voice conversation: talk directly to this specific AI
  // instead of dictating into a shared composer. While active, each final
  // transcript goes straight to this tile (not broadcast, not the global
  // bar), and its reply gets spoken back — genuinely a voice channel to
  // one AI, not just speech-to-text.
  const [voiceModeActive, setVoiceModeActive] = useState(false);
  const voiceModeActiveRef = useRef(false);
  useEffect(() => {
    voiceModeActiveRef.current = voiceModeActive;
  }, [voiceModeActive]);

  const proactiveTriggers = useMemo<ProactiveTrigger[]>(
    () => [
      {
        id: `${tileId}-idle`,
        type: "idle",
        reason: `${profile.name} wants to check in`,
        idleMs: 120000,
      },
    ],
    [tileId, profile.name]
  );

  const handleRaiseHand = (hand: RaisedHand) => {
    eventBus.emit("TILE:RAISE_HAND", hand);
  };

  // Drives the tile's urgency glow (see SpatialTile.tsx) — a response that
  // finished while this tile wasn't the one being looked at should say so
  // visually instead of sitting there silently until someone happens to
  // click back to it.
  const handleMessageComplete = () => {
    if (!isActiveRef.current) {
      eventBus.emit("SPATIAL:TILE_HAS_UNSEEN", { id: tileId });
    }
  };

  useEffect(() => {
    const unsubBroadcast = eventBus.on("SPATIAL:TILE_BROADCAST", ({ message, tileIds }: { message: string; tileIds?: string[] }) => {
      if (!tileIds || tileIds.includes(tileId)) {
        setExternalTrigger({ message, nonce: Date.now() });
      }
    });
    const unsubProactive = eventBus.on("SPATIAL:PROACTIVE_SPEAK", ({ tileId: targetTileId, message }: { tileId: string; message?: string }) => {
      if (targetTileId === tileId) {
        const prompt = message || `${profile.name}, do you have anything to add?`;
        setExternalTrigger({ message: prompt, nonce: Date.now() });
      }
    });
    // The chat-mode header (name + sticky toggle) duplicated what the
    // consciousness bar now shows for whichever tile is active — a thin
    // drag-handle strip on the tile itself (see SpatialTile.tsx) carries the
    // name instead, and every control, including this sticky-note toggle,
    // comes from the bar (see KarmaDynamicIsland.tsx). Only sticky mode
    // keeps its own header now, since it has a color picker that's genuinely
    // local to that view.
    const unsubActiveAction = eventBus.on(
      "SPATIAL:ACTIVE_TILE_ACTION",
      ({ id, action }: { id: string; action: string }) => {
        if (id === tileId && action === "toggleSticky") {
          setStickyMode((s) => !s);
        }
      }
    );
    // When this tile is maximized, its own composer is redundant with the
    // global bottom bar (which the shell now targets at this specific tile
    // instead of broadcasting — see handleSend in MindalooShell.tsx) — hide
    // it to actually reclaim the space instead of showing two input rows.
    const unsubActive = eventBus.on(
      "SPATIAL:ACTIVE_TILE_CHANGED",
      ({ tile }: { tile: { id: string; maximized: boolean } | null }) => {
        setIsMaximized(!!tile && tile.id === tileId && tile.maximized);
        isActiveRef.current = !!tile && tile.id === tileId;
      }
    );
    // Same redundancy shows up in split/grid view too, just with 2+ tiles
    // each drawing their own composer on top of the one global bar — three
    // input rows competing for space on a screen that's already halved.
    // Clicking a tile brings it to front/active, which is what the global
    // bar targets, so there's still exactly one way to type to each tile.
    const unsubCount = eventBus.on("SPATIAL:TILES_COUNT", ({ count }: { count: number }) => {
      setTileCount(count);
    });
    // Speaks this tile's replies back while voice mode is on — persistTileMemory
    // in ChatInterface.tsx emits this for every completed exchange, tileId
    // included, so this is a real signal, not a guess at when a reply landed.
    const unsubExchange = eventBus.on(
      "TILE:EXCHANGE",
      ({ tileId: exchangeTileId, assistantText }: { tileId: string; assistantText: string }) => {
        if (exchangeTileId !== tileId || !voiceModeActiveRef.current) return;
        if (!assistantText?.trim() || typeof window === "undefined" || !window.speechSynthesis) return;
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(assistantText);
        window.speechSynthesis.speak(utterance);
      }
    );
    // Council deliberation (see runCouncilDeliberation in
    // lib/mindaloo/councilDeliberation.ts) happens off-screen — this tile
    // just shows a lightweight "conferring" status while it's in flight,
    // then the one collective verdict lands directly as a reply (no further
    // model call, no visible round-by-round transcript).
    const unsubConferring = eventBus.on(
      "SPATIAL:CONFERRING",
      ({ tileId: targetId, label }: { tileId: string; label: string | null }) => {
        if (targetId === tileId) setConferringLabel(label);
      }
    );
    const unsubInject = eventBus.on(
      "SPATIAL:INJECT_ASSISTANT_MESSAGE",
      ({ tileId: targetId, content }: { tileId: string; content: string }) => {
        if (targetId === tileId) setInjectedAssistantMessage({ content, nonce: Date.now() });
      }
    );
    const unsubInjectUser = eventBus.on(
      "SPATIAL:INJECT_USER_MESSAGE",
      ({ tileId: targetId, content }: { tileId: string; content: string }) => {
        if (targetId === tileId) setInjectedUserMessage({ content, nonce: Date.now() });
      }
    );
    return () => {
      unsubBroadcast();
      unsubProactive();
      unsubActiveAction();
      unsubActive();
      unsubExchange();
      unsubConferring();
      unsubInject();
      unsubInjectUser();
      unsubCount();
    };
  }, [tileId]);

  return (
    <div
      className="h-full flex flex-col overflow-hidden relative"
      style={{ background: "rgb(17 18 27 / var(--mindaloo-tile-opacity, 0.98))" }}
    >
      {/* Chat-mode header removed entirely — the consciousness bar now shows
          the active tile's name and every control, including the sticky
          toggle (see KarmaDynamicIsland.tsx), and SpatialTile.tsx's own thin
          drag-handle strip carries the name for tiles that aren't active.
          Sticky-note mode used to keep a full name+controls header of its
          own here too — harmless when the top bar only showed a dot, but
          once the top bar started showing the tile's name/controls as its
          default content, this became a second header directly under the
          first showing the same name a second time ("the notes button
          clashes"). The color picker is the only thing here that's
          genuinely local to this view (switching back to chat and the tile
          name are both already in the top bar), so it's a small floating
          button instead of a full header bar. */}
      {stickyMode && (
      <div className="absolute top-2 right-2 z-10">
        <button
          type="button"
          onClick={() => setShowColorPicker((p) => !p)}
          className="p-1.5 rounded-lg hover:bg-black/10 transition"
          style={{ background: "rgba(0,0,0,0.08)" }}
          aria-label="Choose sticky note color"
          title="Choose sticky note color"
        >
          <Palette className="w-3.5 h-3.5" style={{ color: stickyColor.text }} />
        </button>
        <AnimatePresence>
          {showColorPicker && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="absolute top-9 right-0 z-50 p-2 rounded-xl grid grid-cols-4 gap-1.5"
              style={{ background: "rgba(10,10,18,0.97)", border: "1px solid rgba(255,255,255,0.1)", minWidth: 112 }}
            >
              {STICKY_COLORS.map((c) => (
                <button
                  key={c.label}
                  type="button"
                  title={c.label}
                  onClick={() => { setStickyColor(c); setShowColorPicker(false); haptics.click(); }}
                  className="w-6 h-6 rounded-md border-2 hover:scale-110 transition"
                  style={{ background: c.bg, borderColor: stickyColor.label === c.label ? accent : "transparent" }}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      )}

      {/* Real per-tile voice conversation — talk directly to this specific
          AI. Distinct from the composer's own dictate-then-send mic (which
          only appears once its collapsed "Message…" row is tapped open —
          see ChatInterface.tsx's composerExpanded): tapping this starts a
          continuous listen/respond/speak loop scoped to this one tile, not
          the shared composer or the global bar. Every AI having its own
          tuned personality is exactly why this needs to be a direct
          channel, not routed through whichever tile happens to be
          "active." */}
      {!stickyMode && (
        <div
          className="absolute top-2 right-2 z-10 rounded-lg p-0.5"
          style={{ background: voiceModeActive ? `${accent}30` : "rgba(0,0,0,0.25)" }}
          title={voiceModeActive ? `Listening — talking to ${profile.name}` : `Talk to ${profile.name}`}
        >
          <VoiceInputButton
            autoRestart
            color={accent}
            onListeningChange={(listening) => {
              setVoiceModeActive(listening);
              if (!listening && typeof window !== "undefined" && window.speechSynthesis) {
                window.speechSynthesis.cancel();
              }
            }}
            onFinalTranscript={(text) => {
              if (!text.trim()) return;
              eventBus.emit("SPATIAL:SEND_TO_TILE", { tileId, message: text });
            }}
            onError={(msg) => eventBus.emit("TOAST:SHOW", { message: `Voice: ${msg}`, type: "error" })}
          />
        </div>
      )}

      {/* Content: full-bleed, no inner borders or padding. */}
      {stickyMode ? (
        <textarea
          className="flex-1 resize-none bg-transparent px-3 py-3 text-sm leading-relaxed focus:outline-none"
          style={{ background: stickyColor.bg, color: stickyColor.text, caretColor: stickyColor.text }}
          placeholder="Jot anything here…"
          value={stickyNote}
          onChange={(e) => setStickyNote(e.target.value)}
        />
      ) : (
        <div className="flex-1 min-h-0 overflow-hidden relative">
          {conferringLabel && (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 px-6 text-center" style={{ background: "rgba(10,10,18,0.92)" }}>
              <span className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: `${accent}40`, borderTopColor: accent }} />
              <p className="text-xs text-white/70">{conferringLabel}</p>
            </div>
          )}
          <ChatInterface
            profile={profile}
            initialPrompt={initialPrompt}
            hideSidebar
            karmaOsMode
            autoSendPrompt={!!initialPrompt?.trim()}
            chatWindowId={tileId}
            connectedChatIds={connectedChatIds}
            primaryTileId={primaryTileId}
            externalTrigger={externalTrigger}
            injectedAssistantMessage={injectedAssistantMessage}
            injectedUserMessage={injectedUserMessage}
            theme={theme}
            tileName={profile.name}
            proactiveTriggers={proactiveTriggers}
            onRaiseHand={handleRaiseHand}
            onMessageComplete={handleMessageComplete}
          />
        </div>
      )}
    </div>
  );
});
